import React, { useState } from "react";
import { Container, Row, Col, Card, Button, Alert } from "react-bootstrap";
import { ArrowLeft, CreditCard, CheckCircle } from "lucide-react";
import CheckoutOptions from "../components/CheckoutOptions";
import { useNavigate } from "react-router-dom";

const CheckoutDemo: React.FC = () => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState(false);

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
    setShowSuccess(false);
  };

  const handleProceedToPayment = () => {
    if (selectedOption) {
      setShowSuccess(true);
      // In a real app, you would redirect to payment processing
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    }
  };

  const getSelectedOptionName = () => {
    const options = {
      "credit-card": "Credit/Debit Card",
      "bank-transfer": "Bank Transfer",
      "digital-wallet": "Digital Wallet",
      crypto: "Cryptocurrency",
    };
    return options[selectedOption as keyof typeof options] || "";
  };

  return (
    <div className="min-vh-100">

      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10}>
            {/* Back Button */}
            <Button
              variant="outline-secondary"
              className="mb-4"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} className="me-2" />
              Back
            </Button>

            {/* Page Header */}
            <div className="text-center mb-5">
              <h1 className="display-6 fw-bold text-primary mb-3">
                Complete Your Purchase
              </h1>
              <p className="lead text-muted">
                Choose your preferred payment method to continue
              </p>
            </div>

            {/* Order Summary */}
            <Card className="mb-4">
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="mb-2">Order Summary</h5>
                    <div className="d-flex justify-content-between">
                      <span>Premium Trading Plan</span>
                      <span className="fw-bold">$99.00/month</span>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Setup Fee</span>
                      <span>$0.00</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between h5 mb-0">
                      <span>Total</span>
                      <span className="text-primary">$99.00</span>
                    </div>
                  </Col>
                  <Col md={4} className="text-center">
                    <div
                      className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <CreditCard size={32} className="text-primary" />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Checkout Options */}
            <CheckoutOptions
              onOptionSelect={handleOptionSelect}
              selectedOption={selectedOption}
            />

            {/* Success Message */}
            {showSuccess && (
              <Alert variant="success" className="mt-4">
                <CheckCircle size={20} className="me-2" />
                <strong>Payment Method Selected!</strong> Redirecting to{" "}
                {getSelectedOptionName()} payment...
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-between mt-5">
              <Button
                variant="outline-secondary"
                size="lg"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                size="lg"
                onClick={handleProceedToPayment}
                disabled={!selectedOption}
                className="px-5"
              >
                {selectedOption
                  ? `Pay with ${getSelectedOptionName()}`
                  : "Select Payment Method"}
              </Button>
            </div>

            {/* Security Notice */}
            <Card className="mt-4">
              <Card.Body className="text-center py-3">
                <small className="text-muted">
                  <strong>🔒 Secure Payment:</strong> All transactions are
                  encrypted and processed securely. We never store your payment
                  information.
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CheckoutDemo;
